import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UploadSimple, FileText, Trash } from "@phosphor-icons/react";
import PageHeader from "@/components/PageHeader";

const ACCEPT = ".pdf,.docx,.xlsx,.csv,.pptx,.txt";

export default function KnowledgeBase() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get("/kb").then((r) => setDocs(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/kb/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded: ${file.name}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) upload(f);
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this document?")) return;
    try { await api.delete(`/kb/${id}`); toast.success("Removed"); load(); } catch { toast.error("Failed"); }
  };

  return (
    <div className="p-4 sm:p-8 lg:p-12 max-w-7xl mx-auto" data-testid="kb-page">
      <PageHeader
        eyebrow="Knowledge Base"
        title="Organisation context"
        subtitle="Upload documents your agents and Copilot can use as proprietary context. PDF · DOCX · XLSX · CSV · PPTX."
      />

      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        className="mt-8 bg-card border-2 border-dashed border-border rounded-2xl p-8 sm:p-12 text-center hover:border-primary/40 hover:bg-teal-soft/30 transition-colors"
        data-testid="kb-dropzone"
      >
        <div className="inline-flex h-14 w-14 bg-teal-soft text-teal rounded-2xl items-center justify-center mb-4">
          <UploadSimple size={26} weight="bold" />
        </div>
        <div className="font-display text-lg sm:text-xl">Drop a document or click to upload</div>
        <div className="text-xs sm:text-sm text-muted-foreground mt-2">Max 25MB per file. Stays inside your organisation.</div>
        <button onClick={() => inputRef.current?.click()} disabled={uploading} className="mt-6 inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60 card-elev" data-testid="kb-upload-btn">
          {uploading ? "Uploading…" : "Select file"}
        </button>
        <input ref={inputRef} type="file" accept={ACCEPT} className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="kb-file-input" />
      </div>

      <div className="mt-10">
        <h2 className="font-display text-xl sm:text-2xl mb-4">Documents</h2>
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          {loading && <div className="p-6 text-sm text-muted-foreground">Loading…</div>}
          {!loading && docs.length === 0 && <div className="p-10 sm:p-12 text-center text-sm text-muted-foreground" data-testid="kb-empty">No documents yet.</div>}
          {docs.map((d) => (
            <div key={d.id} className="flex items-center gap-3 p-4 border-b border-border last:border-b-0 hover:bg-secondary/50 transition-colors" data-testid={`kb-doc-${d.id}`}>
              <div className="h-9 w-9 rounded-lg bg-blue-soft text-blue grid place-items-center flex-shrink-0">
                <FileText size={16} weight="bold" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{d.original_filename}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{(d.size / 1024).toFixed(0)} KB · {d.content_type}</div>
              </div>
              <button onClick={() => remove(d.id)} className="text-muted-foreground hover:text-destructive transition-colors p-1.5" aria-label="Delete" data-testid={`kb-delete-${d.id}`}>
                <Trash size={16} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
